import { useCallback, useEffect, useState, type ReactNode } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import type {
  Customer360Record,
  CustomerCrmNote,
  CustomerCrmProfile,
  CustomerCrmTask,
  Localized,
} from '@servicesai/api-client';
import {
  DataTable,
  EmptyState,
  FilterBar,
  GlassButton,
  GlassCard,
  GlassInput,
  GlassSelect,
  GlassTextarea,
  MetricCard,
  StatusPill,
  formatDate,
  formatFils,
  useI18n,
  type DataTableColumn,
} from '@servicesai/ui';
import { admin } from '../lib/api.js';
import { S } from '../strings.js';
import { HeroBand, MetricTile, SectionCard } from '../components/elevated/kit.js';
import { Icon } from '../components/elevated/icons.js';
import { SECTION_ACCENTS } from '../lib/accents.js';
import { CxoneBadge, CxoneCrmBanner } from '../components/CxoneBrand.js';
import { MinistryCrmTab } from '../components/MinistryCrmTab.js';

type TabId =
  | 'overview'
  | 'tickets'
  | 'conversations'
  | 'activity'
  | 'notes'
  | 'tasks'
  | 'feedback'
  | 'payments'
  | 'documents'
  | 'identity'
  | 'ministry_crm';

const TABS: { id: TabId; label: Localized }[] = [
  { id: 'overview', label: { en: 'Overview', ar: 'نظرة عامة' } },
  { id: 'tickets', label: { en: 'Tickets', ar: 'التذاكر' } },
  { id: 'conversations', label: { en: 'Conversations', ar: 'المحادثات' } },
  { id: 'activity', label: { en: 'Activity', ar: 'النشاط' } },
  { id: 'notes', label: { en: 'Notes', ar: 'الملاحظات' } },
  { id: 'tasks', label: { en: 'Tasks', ar: 'المهام' } },
  { id: 'feedback', label: { en: 'Feedback', ar: 'الملاحظات والشكاوى' } },
  { id: 'payments', label: { en: 'Payments', ar: 'المدفوعات' } },
  { id: 'documents', label: { en: 'Documents', ar: 'المستندات' } },
  { id: 'identity', label: { en: 'Identity & consent', ar: 'الهوية والموافقات' } },
  { id: 'ministry_crm', label: { en: 'Ministry CRM', ar: 'النظام الوزاري' } },
];

const EMIRATE_LABELS: Record<string, Localized> = {
  abu_dhabi: { en: 'Abu Dhabi', ar: 'أبوظبي' },
  dubai: { en: 'Dubai', ar: 'دبي' },
  sharjah: { en: 'Sharjah', ar: 'الشارقة' },
  ajman: { en: 'Ajman', ar: 'عجمان' },
  umm_al_quwain: { en: 'Umm Al Quwain', ar: 'أم القيوين' },
  ras_al_khaimah: { en: 'Ras Al Khaimah', ar: 'رأس الخيمة' },
  fujairah: { en: 'Fujairah', ar: 'الفجيرة' },
};

const ACTIVE_APPLICATION_STATES = new Set([
  'intake',
  'eligibility',
  'data_collection',
  'documents',
  'review_summary',
  'payment',
  'submitting',
  'submitted',
  'in_review',
  'needs_citizen_input',
]);

function relationshipHealth(customer: Customer360Record): number {
  const openTickets = customer.applications.filter((app) =>
    ACTIVE_APPLICATION_STATES.has(app.state),
  ).length;
  const openTasks = customer.tasks.filter(
    (task) => task.status === 'open' || task.status === 'in_progress',
  ).length;
  const openComplaints = customer.complaints.filter(
    (complaint) => complaint.status === 'open' || complaint.status === 'escalated',
  ).length;
  const average =
    customer.satisfaction.length > 0
      ? customer.satisfaction.reduce((sum, survey) => sum + survey.score, 0) /
        customer.satisfaction.length
      : 3;
  return Math.max(
    0,
    Math.min(
      100,
      Math.round(
        92 -
          openTickets * 6 -
          openTasks * 4 -
          openComplaints * 18 -
          (customer.crm.riskLevel === 'critical'
            ? 30
            : customer.crm.riskLevel === 'elevated'
              ? 15
              : 0) +
          (average - 3) * 8,
      ),
    ),
  );
}

function CrmIcon({ name, className = 'h-4 w-4' }: { name: string; className?: string }) {
  const paths: Record<string, ReactNode> = {
    phone: <path d="M7 3H5a2 2 0 0 0-2 2c0 8.8 7.2 16 16 16a2 2 0 0 0 2-2v-2l-4-1.5-1 2.5c-3.4-1.5-6.5-4.6-8-8L10.5 7 9 3Z" />,
    email: <><rect x="3" y="5" width="18" height="14" rx="2" /><path d="m4 7 8 6 8-6" /></>,
    identity: <><rect x="3" y="5" width="18" height="14" rx="2" /><circle cx="9" cy="11" r="2" /><path d="M6 16c.7-1.6 1.7-2.4 3-2.4s2.3.8 3 2.4M14 10h4M14 14h4" /></>,
    ticket: <><path d="M4 5h16v5a2 2 0 0 0 0 4v5H4v-5a2 2 0 0 0 0-4Z" /><path d="M9 5v14" /></>,
    conversation: <><path d="M21 12a8 8 0 0 1-8 8H5l-3 2 1-5a9 9 0 1 1 18-5Z" /></>,
    note: <><path d="M5 3h14a2 2 0 0 1 2 2v10l-6 6H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2Z" /><path d="M15 21v-6h6M7 8h10M7 12h7" /></>,
    task: <><rect x="4" y="3" width="16" height="18" rx="2" /><path d="m8 9 2 2 4-4M8 16h8" /></>,
    document: <><path d="M6 2h8l4 4v16H6Z" /><path d="M14 2v5h5M9 12h6M9 16h6" /></>,
    consent: <><path d="M12 22s8-4 8-11V5l-8-3-8 3v6c0 7 8 11 8 11Z" /><path d="m9 12 2 2 4-5" /></>,
    activity: <><path d="M3 12h4l2-7 4 14 2-7h6" /></>,
    pin: <><path d="m9 3 6 6M7 8l4 4-6 6M13 2l9 9M10 11l-7 7" /></>,
  };
  return (
    <svg
      aria-hidden
      viewBox="0 0 24 24"
      className={className}
      fill="none"
      style={{ fill: 'none' }}
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      {paths[name] ?? <circle cx="12" cy="12" r="8" />}
    </svg>
  );
}

function SectionTitle({
  icon,
  title,
  count,
  action,
}: {
  icon: string;
  title: string;
  count?: number;
  action?: ReactNode;
}) {
  return (
    <div className="mb-3 flex flex-wrap items-center gap-2">
      <span className="grid h-8 w-8 place-items-center rounded-lg bg-aeblack-50 text-ink-soft">
        <CrmIcon name={icon} />
      </span>
      <h2 className="text-base font-extrabold">{title}</h2>
      {count !== undefined && <span className="aegov-badge">{count}</span>}
      {action && <div className="ms-auto">{action}</div>}
    </div>
  );
}

export function CustomerPage() {
  const { id = '' } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { l, lang } = useI18n();
  const requestedTab = searchParams.get('tab') as TabId | null;
  const activeTab = TABS.some((tab) => tab.id === requestedTab) ? requestedTab! : 'overview';
  const [customer, setCustomer] = useState<Customer360Record | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [showCrmEditor, setShowCrmEditor] = useState(false);

  const load = useCallback(async () => {
    try {
      setError(null);
      setCustomer(await admin().customer(id));
    } catch (loadError) {
      setError((loadError as Error).message);
    }
  }, [id]);

  useEffect(() => { void load(); }, [load]);

  const setTab = (tab: TabId) => {
    setSearchParams(tab === 'overview' ? {} : { tab });
  };

  const act = async (run: () => Promise<unknown>, message: Localized) => {
    setBusy(true);
    try {
      await run();
      setToast(l(message));
      await load();
    } catch (actionError) {
      setToast(`${l(S.actionFailed)}: ${(actionError as Error).message}`);
    } finally {
      setBusy(false);
    }
  };

  if (error) {
    return <div role="alert" className="aegov-alert alert-error"><div className="alert-content">{error}</div></div>;
  }
  if (!customer) {
    return <div className="h-[32rem] animate-pulse rounded-xl border border-aeblack-100 bg-white" aria-busy="true" />;
  }

  const displayName =
    (lang === 'ar' ? customer.name?.ar : customer.name?.en) ??
    customer.name?.en ??
    customer.name?.ar ??
    l({ en: 'Unnamed customer', ar: 'متعامل بلا اسم' });
  const initials = displayName
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0])
    .join('')
    .toUpperCase();
  const activeTickets = customer.applications.filter((app) => ACTIVE_APPLICATION_STATES.has(app.state));
  const openTasks = customer.tasks.filter((task) => task.status === 'open' || task.status === 'in_progress');
  const lastActivityAt = customer.timeline[0]?.at ?? customer.createdAt;
  const health = relationshipHealth(customer);
  const openComplaints = customer.complaints.filter(
    (complaint) => complaint.status === 'open' || complaint.status === 'escalated',
  ).length;
  const satisfactionAverage =
    customer.satisfaction.length > 0
      ? customer.satisfaction.reduce((sum, survey) => sum + survey.score, 0) /
        customer.satisfaction.length
      : null;
  const cxoneConversationCount = customer.conversations.filter(
    (conversation) => conversation.cxone,
  ).length;
  const emirate =
    typeof customer.profile.emirate === 'string' ? customer.profile.emirate : null;
  const maritalStatus =
    typeof customer.profile.marital_status === 'string' ? customer.profile.marital_status : null;
  const applicantCategory =
    typeof customer.profile.applicant_category === 'string'
      ? customer.profile.applicant_category
      : null;
  const numberOfWives =
    customer.profile.number_of_wives != null ? String(customer.profile.number_of_wives) : null;
  const numberOfChildren =
    customer.profile.number_of_children != null
      ? String(customer.profile.number_of_children)
      : null;
  const maritalLabels: Record<string, { en: string; ar: string }> = {
    single: { en: 'Single', ar: 'أعزب / عزباء' },
    married: { en: 'Married', ar: 'متزوج / متزوجة' },
    divorced: { en: 'Divorced', ar: 'مطلق / مطلقة' },
    widowed: { en: 'Widowed', ar: 'أرمل / أرملة' },
  };
  const categoryLabels: Record<string, { en: string; ar: string }> = {
    standard: { en: 'Standard case (family breadwinner)', ar: 'حالة عادية (عائل أسرة)' },
    people_of_determination: { en: 'People of Determination', ar: 'أصحاب الهمم' },
    senior_citizen_60_plus: { en: 'Senior citizens (60+)', ar: 'كبار المواطنين (60 فأكثر)' },
    orphan: { en: 'Orphans', ar: 'الأيتام' },
    widowed_mother_custody: { en: 'Widowed mothers with custody', ar: 'الأرامل الحاضنات' },
    divorced_mother_custody: { en: 'Divorced mothers with custody', ar: 'المطلقات الحاضنات' },
    female_unknown_parentage: { en: 'Female of unknown parentage', ar: 'مجهولة النسب' },
    female_orphan_both_parents: {
      en: 'Female orphans (lost both parents)',
      ar: 'اليتيمات فاقدات الأبوين',
    },
    female_married_non_citizen: {
      en: 'Female married to a non-citizen',
      ar: 'المتزوجة من غير مواطن',
    },
  };
  const activeConsents = customer.consents.filter((consent) => !consent.revoked_at).length;

  /* Smart badges (plan point 39): VIP = 3+ approved applications or 5,000+ AED
   * captured lifetime value; at-risk = any open complaint or failed payment. */
  const approvedCount = customer.applications.filter((app) => {
    if (app.state === 'completed' || app.state === 'approved') return true;
    const outcome = (app.decision as { outcome?: unknown } | null)?.outcome;
    return typeof outcome === 'string' && outcome === 'approved';
  }).length;
  const capturedFils = customer.payments
    .filter((payment) => payment.status === 'captured')
    .reduce((sum, payment) => sum + payment.amountFils, 0);
  const failedPayments = customer.payments.filter((payment) => payment.status === 'failed').length;
  const isVip = approvedCount >= 3 || capturedFils >= 500_000;
  const isAtRisk = openComplaints > 0 || failedPayments > 0;
  const vipTooltip = `${l(S.vipRule)} — ${approvedCount} ${l(S.approvedApps)} · ${formatFils(capturedFils, lang)} ${l(S.capturedValue)}`;
  const atRiskTooltip = `${l(S.atRiskRule)} — ${openComplaints} ${l(S.openComplaintsLabel)} · ${failedPayments} ${l(S.failedPaymentsLabel)}`;

  return (
    <div className="pb-12">
      <HeroBand
        accent="#0173ab"
        eyebrow={l({ en: 'Customer 360', ar: 'ملف المتعامل الشامل' })}
        breadcrumbs={
          <>
            <Link to="/customers" className="transition hover:text-white">{l(S.customers)}</Link>
            <span className="mx-1.5" aria-hidden>›</span>
            <span>{displayName}</span>
          </>
        }
        title={
          <span className="flex items-center gap-4">
            <span className="grid h-14 w-14 shrink-0 place-items-center rounded-2xl border border-white/20 bg-white/10 text-xl font-black text-white">
              {initials || '—'}
            </span>
            <span className="flex min-w-0 flex-wrap items-center gap-2">
              <span>{displayName}</span>
              {isVip && (
                <span
                  title={vipTooltip}
                  className="inline-flex items-center gap-1 rounded-full bg-aegold-500/30 px-2.5 py-1 text-[10px] font-extrabold uppercase tracking-wide text-aegold-100"
                >
                  <Icon name="star" size={11} />
                  {l(S.vipBadge)}
                </span>
              )}
              {isAtRisk && (
                <span
                  title={atRiskTooltip}
                  className="inline-flex items-center gap-1 rounded-full bg-aered-500/30 px-2.5 py-1 text-[10px] font-extrabold uppercase tracking-wide text-aered-100"
                >
                  <Icon name="warning" size={11} />
                  {l(S.atRiskBadge)}
                </span>
              )}
            </span>
          </span>
        }
        subtitle={
          <>
            <span className="font-mono text-xs text-white/60" dir="ltr">{customer.id.slice(0, 8)}</span>
            {customer.name?.ar && customer.name.ar !== displayName && (
              <span className="ms-2 font-semibold text-white/85" dir="rtl">{customer.name.ar}</span>
            )}
            <span className="mt-1 block text-white/65">
              {l({ en: 'Customer since', ar: 'متعامل منذ' })}: {formatDate(customer.createdAt, lang)}
            </span>
          </>
        }
        status={
          <>
            <span className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-[10px] font-extrabold uppercase tracking-wide ${customer.verificationTier.startsWith('uaepass') ? 'bg-aegreen-500/25 text-aegreen-100' : 'border border-white/15 bg-white/10 text-white/80'}`}>
              {customer.verificationTier.replaceAll('_', ' ')}
            </span>
            <span className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-[10px] font-extrabold uppercase tracking-wide ${customer.crm.riskLevel === 'critical' ? 'bg-aered-500/30 text-aered-100' : customer.crm.riskLevel === 'elevated' ? 'bg-aegold-500/25 text-aegold-100' : 'border border-white/15 bg-white/10 text-white/80'}`}>
              {customer.crm.riskLevel} {l({ en: 'risk', ar: 'مخاطر' })}
            </span>
            {customer.phone && (
              <span className="inline-flex items-center rounded-full border border-white/15 bg-white/10 px-3 py-1.5 font-mono text-[10px] font-bold text-white/80" dir="ltr">
                {customer.phone}
              </span>
            )}
            {emirate && (
              <span className="inline-flex items-center rounded-full border border-white/15 bg-white/10 px-3 py-1.5 text-[10px] font-extrabold uppercase tracking-wide text-white/80">
                {l(EMIRATE_LABELS[emirate] ?? { en: emirate.replaceAll('_', ' '), ar: emirate.replaceAll('_', ' ') })}
              </span>
            )}
          </>
        }
        actions={
          <>
            <button
              type="button"
              onClick={() => { setTab('notes'); setToast(null); }}
              className="inline-flex min-h-10 cursor-pointer items-center gap-2 rounded-xl border border-white/20 bg-white/10 px-4 text-xs font-extrabold text-white transition hover:bg-white/25"
            >
              <CrmIcon name="note" />
              {l({ en: 'Add note', ar: 'إضافة ملاحظة' })}
            </button>
            <button
              type="button"
              onClick={() => setTab('tasks')}
              className="inline-flex min-h-10 cursor-pointer items-center gap-2 rounded-xl border border-white/20 bg-white/10 px-4 text-xs font-extrabold text-white transition hover:bg-white/25"
            >
              <CrmIcon name="task" />
              {l({ en: 'New task', ar: 'مهمة جديدة' })}
            </button>
            <button
              type="button"
              onClick={() => setShowCrmEditor((value) => !value)}
              className="inline-flex min-h-10 cursor-pointer items-center gap-2 rounded-xl bg-aegold-500 px-4 text-xs font-extrabold text-aeblack-900 shadow-lg transition hover:brightness-110"
            >
              {l({ en: 'Edit CRM profile', ar: 'تعديل ملف CRM' })}
            </button>
          </>
        }
        metrics={
          <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
            <MetricTile
              dark
              label={l({ en: 'Applications', ar: 'الطلبات' })}
              value={customer.applications.length}
              hint={`${activeTickets.length} ${l({ en: 'active now', ar: 'نشطة حالياً' })}`}
              accent="#0173ab"
            />
            <MetricTile
              dark
              label={l({ en: 'Conversations', ar: 'المحادثات' })}
              value={customer.conversations.length}
              hint={cxoneConversationCount > 0 ? `${cxoneConversationCount} CXone` : undefined}
              accent="#0173ab"
            />
            <MetricTile
              dark
              label={l({ en: 'Vault documents', ar: 'مستندات الخزنة' })}
              value={customer.documents.length}
              accent="#0173ab"
            />
            <MetricTile
              dark
              label={l(S.consentsLabel)}
              value={activeConsents}
              hint={`${customer.consents.length} ${l({ en: 'recorded', ar: 'مسجلة' })}`}
              accent="#3f8e50"
            />
          </div>
        }
      />

      {toast && (
        <div className="aegov-alert alert-info mb-4" role="status">
          <div className="alert-content">{toast}</div>
        </div>
      )}

      <div className="mb-4">
        <CxoneCrmBanner
          conversationCount={cxoneConversationCount}
          onOpenQueue={() => navigate('/handover')}
        />
      </div>

      <div className="mb-4 grid gap-4 xl:grid-cols-[minmax(0,1fr)_22rem]">
        <GlassCard className="p-5">
          <div className="flex flex-col gap-5 sm:flex-row sm:items-start">
            <div className="grid h-20 w-20 shrink-0 place-items-center rounded-2xl bg-aeblack-800 text-2xl font-black text-white">
              {initials || '—'}
            </div>
            <div className="min-w-0 flex-1">
              {customer.name?.ar && customer.name.ar !== displayName && (
                <div className="mb-2 text-base font-bold text-ink-soft" dir="rtl">{customer.name.ar}</div>
              )}
              <div className="grid gap-3 text-sm sm:grid-cols-2 xl:grid-cols-3">
                <ContactDatum icon="phone" label={l({ en: 'Phone', ar: 'الهاتف' })} value={customer.phone} dir="ltr" />
                <ContactDatum icon="email" label={l({ en: 'Email', ar: 'البريد الإلكتروني' })} value={customer.email} dir="ltr" />
                <ContactDatum icon="identity" label={l({ en: 'Emirates ID', ar: 'الهوية الإماراتية' })} value={customer.emiratesId} dir="ltr" />
                <ContactDatum icon="consent" label={l({ en: 'Preferred language', ar: 'اللغة المفضلة' })} value={customer.preferredLang.toUpperCase()} />
                <ContactDatum icon="activity" label={l({ en: 'Customer since', ar: 'متعامل منذ' })} value={formatDate(customer.createdAt, lang)} />
                <ContactDatum icon="task" label={l({ en: 'CRM owner', ar: 'مسؤول العلاقة' })} value={customer.crm.ownerName ?? l({ en: 'Unassigned', ar: 'غير معيّن' })} />
                {maritalStatus && (
                  <ContactDatum
                    icon="identity"
                    label={l({ en: 'Marital status', ar: 'الحالة الاجتماعية' })}
                    value={l(maritalLabels[maritalStatus] ?? { en: maritalStatus, ar: maritalStatus })}
                  />
                )}
                {applicantCategory && (
                  <ContactDatum
                    icon="identity"
                    label={l({ en: 'Applicant category', ar: 'فئة مقدم الطلب' })}
                    value={l(
                      categoryLabels[applicantCategory] ?? {
                        en: applicantCategory,
                        ar: applicantCategory,
                      },
                    )}
                  />
                )}
                {numberOfWives != null && (
                  <ContactDatum
                    icon="identity"
                    label={l({ en: 'Number of wives', ar: 'عدد الزوجات' })}
                    value={numberOfWives}
                  />
                )}
                {numberOfChildren != null && (
                  <ContactDatum
                    icon="identity"
                    label={l({ en: 'Number of children', ar: 'عدد الأبناء' })}
                    value={numberOfChildren}
                  />
                )}
              </div>
              {customer.crm.tags.length > 0 && (
                <div className="mt-4 flex flex-wrap gap-1.5">
                  {customer.crm.tags.map((tag) => <span key={tag} className="aegov-badge">{tag}</span>)}
                </div>
              )}
            </div>
          </div>
        </GlassCard>

        <GlassCard className="relative overflow-hidden p-0">
          <div
            className={`h-1.5 ${
              health >= 80 ? 'bg-approve' : health >= 55 ? 'bg-review' : 'bg-reject'
            }`}
          />
          <div className="p-5">
            <div className="flex items-center gap-4">
              <div
                className="grid h-20 w-20 shrink-0 place-items-center rounded-full"
                style={{
                  background: `conic-gradient(${
                    health >= 80 ? '#2f663c' : health >= 55 ? '#b2550a' : '#b52520'
                  } ${health * 3.6}deg, #e1e3e5 0deg)`,
                }}
              >
                <div className="grid h-16 w-16 place-items-center rounded-full bg-white text-center">
                  <div>
                    <div className="font-mono text-xl font-black">{health}%</div>
                    <div className="text-[8px] font-bold tracking-wide text-ink-faint uppercase">
                      {l({ en: 'Health', ar: 'الصحة' })}
                    </div>
                  </div>
                </div>
              </div>
              <div className="min-w-0 flex-1">
                <div className="text-[10px] font-bold tracking-wide text-ink-faint uppercase">
                  {l({ en: 'Relationship intelligence', ar: 'ذكاء العلاقة' })}
                </div>
                <div className="mt-1 text-sm font-extrabold">
                  {health >= 80
                    ? l({ en: 'Healthy relationship', ar: 'علاقة جيدة' })
                    : health >= 55
                      ? l({ en: 'Monitor closely', ar: 'تحتاج إلى مراقبة' })
                      : l({ en: 'Intervention required', ar: 'تحتاج إلى تدخل' })}
                </div>
                <div className="mt-2 flex flex-wrap gap-1">
                  <span className="aegov-badge">{customer.crm.lifecycleStage.replaceAll('_', ' ')}</span>
                  <span className="aegov-badge">{customer.crm.riskLevel} risk</span>
                </div>
              </div>
            </div>
            <div className="mt-4 border-t border-aeblack-100 pt-3">
              <div className="text-[10px] font-bold tracking-wide text-ink-faint uppercase">
                {l({ en: 'Next best action', ar: 'الإجراء التالي' })}
              </div>
              <div className="mt-1 text-sm font-extrabold">
                {customer.crm.nextFollowUpAt
                  ? l({ en: 'Scheduled follow-up', ar: 'متابعة مجدولة' })
                  : activeTickets.length > 0
                    ? l({ en: 'Review active tickets', ar: 'مراجعة التذاكر النشطة' })
                    : openTasks.length > 0
                      ? l({ en: 'Complete open tasks', ar: 'إنجاز المهام المفتوحة' })
                      : l({ en: 'No immediate action', ar: 'لا يوجد إجراء فوري' })}
              </div>
              <p className="mt-1 text-xs leading-relaxed text-ink-soft">
                {customer.crm.nextFollowUpAt
                  ? formatDate(customer.crm.nextFollowUpAt, lang)
                  : `${activeTickets.length} ${l({ en: 'active tickets', ar: 'تذاكر نشطة' })} · ${openTasks.length} ${l({ en: 'open tasks', ar: 'مهام مفتوحة' })}`}
              </p>
            </div>
            <div className="mt-3 text-[10px] text-ink-faint">
              {l({ en: 'Last interaction', ar: 'آخر تفاعل' })}: {formatDate(lastActivityAt, lang)}
            </div>
          </div>
        </GlassCard>
      </div>

      {showCrmEditor && (
        <CrmProfileEditor
          customer={customer}
          busy={busy}
          onCancel={() => setShowCrmEditor(false)}
          onSave={(input) => act(
            () => admin().updateCustomerCrm(customer.id, input),
            { en: 'CRM profile updated', ar: 'تم تحديث ملف CRM' },
          ).then(() => setShowCrmEditor(false))}
        />
      )}

      <div className="mb-4 grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
        <MetricCard label={l({ en: 'Open tasks', ar: 'المهام المفتوحة' })} value={openTasks.length} tone={openTasks.length ? 'warning' : 'success'} />
        <MetricCard label={l({ en: 'Internal notes', ar: 'الملاحظات الداخلية' })} value={customer.notes.length} />
        <MetricCard label={l({ en: 'Open complaints', ar: 'الشكاوى المفتوحة' })} value={openComplaints} tone={openComplaints ? 'danger' : 'success'} />
        <MetricCard label={l({ en: 'Customer pulse', ar: 'نبض المتعامل' })} value={satisfactionAverage === null ? '—' : `${satisfactionAverage.toFixed(1)}/5`} tone={satisfactionAverage !== null && satisfactionAverage < 3 ? 'danger' : 'success'} />
      </div>

      <FilterBar className="sticky top-[4.75rem] z-20 overflow-x-auto !flex-nowrap shadow-sm">
        {TABS.map((tab) => {
          const count =
            tab.id === 'tickets' ? customer.applications.length
              : tab.id === 'conversations' ? customer.conversations.length
                : tab.id === 'notes' ? customer.notes.length
                  : tab.id === 'tasks' ? customer.tasks.length
                    : tab.id === 'feedback' ? customer.complaints.length + customer.satisfaction.length
                      : tab.id === 'payments' ? customer.payments.length
                    : tab.id === 'documents' ? customer.documents.length
                      : undefined;
          return (
            <button
              key={tab.id}
              type="button"
              onClick={() => setTab(tab.id)}
              aria-current={activeTab === tab.id ? 'page' : undefined}
              className={[
                'inline-flex min-h-10 shrink-0 items-center gap-2 rounded-lg px-3 text-xs font-bold transition',
                activeTab === tab.id
                  ? 'bg-aeblack-800 text-white'
                  : 'text-ink-soft hover:bg-aeblack-50 hover:text-ink',
              ].join(' ')}
            >
              {l(tab.label)}
              {count !== undefined && (
                <span className={activeTab === tab.id ? 'text-aeblack-200' : 'text-ink-faint'}>{count}</span>
              )}
            </button>
          );
        })}
      </FilterBar>

      {activeTab === 'overview' && (
        <OverviewTab
          customer={customer}
          onTab={setTab}
          onOpenTicket={(reference) => navigate(`/cases/${reference}`)}
          onOpenConversation={(conversationId) => navigate(`/conversations/${conversationId}`)}
        />
      )}
      {activeTab === 'tickets' && (
        <TicketsTab customer={customer} onOpen={(reference) => navigate(`/cases/${reference}`)} />
      )}
      {activeTab === 'conversations' && (
        <ConversationsTab customer={customer} onOpen={(conversationId) => navigate(`/conversations/${conversationId}`)} />
      )}
      {activeTab === 'activity' && <ActivityTab customer={customer} />}
      {activeTab === 'notes' && (
        <NotesTab
          customer={customer}
          busy={busy}
          onCreate={(input) => act(
            () => admin().createCustomerNote(customer.id, input),
            { en: 'Internal note added', ar: 'تمت إضافة الملاحظة الداخلية' },
          )}
          onPin={(note) => act(
            () => admin().pinCustomerNote(customer.id, note.id, !note.pinned),
            note.pinned
              ? { en: 'Note unpinned', ar: 'تم إلغاء تثبيت الملاحظة' }
              : { en: 'Note pinned', ar: 'تم تثبيت الملاحظة' },
          )}
        />
      )}
      {activeTab === 'tasks' && (
        <TasksTab
          customer={customer}
          busy={busy}
          onCreate={(input) => act(
            () => admin().createCustomerTask(customer.id, input),
            { en: 'Follow-up task created', ar: 'تم إنشاء مهمة المتابعة' },
          )}
          onStatus={(task, status) => act(
            () => admin().updateCustomerTaskStatus(customer.id, task.id, status),
            { en: 'Task updated', ar: 'تم تحديث المهمة' },
          )}
        />
      )}
      {activeTab === 'feedback' && <FeedbackTab customer={customer} />}
      {activeTab === 'payments' && <PaymentsTab customer={customer} />}
      {activeTab === 'documents' && <DocumentsTab customer={customer} />}
      {activeTab === 'identity' && <IdentityTab customer={customer} />}
      {activeTab === 'ministry_crm' && <MinistryCrmTab customerId={customer.id} />}
    </div>
  );
}

function ContactDatum({
  icon,
  label,
  value,
  dir,
}: {
  icon: string;
  label: string;
  value: string | null;
  dir?: 'ltr' | 'rtl';
}) {
  return (
    <div className="flex min-w-0 items-start gap-2.5">
      <span className="mt-0.5 text-ink-faint"><CrmIcon name={icon} /></span>
      <div className="min-w-0">
        <div className="text-[10px] font-bold tracking-wide text-ink-faint uppercase">{label}</div>
        <div className="truncate font-semibold text-ink" dir={dir}>{value ?? '—'}</div>
      </div>
    </div>
  );
}

function OverviewTab({
  customer,
  onTab,
  onOpenTicket,
  onOpenConversation,
}: {
  customer: Customer360Record;
  onTab: (tab: TabId) => void;
  onOpenTicket: (reference: string) => void;
  onOpenConversation: (id: string) => void;
}) {
  const { l, lang } = useI18n();
  const activeTickets = customer.applications.filter((app) => ACTIVE_APPLICATION_STATES.has(app.state));
  const pinnedNotes = customer.notes.filter((note) => note.pinned);
  const openTasks = customer.tasks.filter((task) => task.status === 'open' || task.status === 'in_progress');
  return (
    <div className="grid gap-4 xl:grid-cols-2">
      <RelationshipTimelineCard customer={customer} />
      <GlassCard className="p-5">
        <SectionTitle
          icon="ticket"
          title={l({ en: 'Active tickets', ar: 'التذاكر النشطة' })}
          count={activeTickets.length}
          action={<GlassButton size="sm" variant="ghost" onClick={() => onTab('tickets')}>{l({ en: 'View all', ar: 'عرض الكل' })}</GlassButton>}
        />
        <div className="space-y-2">
          {activeTickets.slice(0, 5).map((ticket) => (
            <button
              key={ticket.reference}
              type="button"
              onClick={() => onOpenTicket(ticket.reference)}
              className="flex min-h-14 w-full items-center gap-3 rounded-lg border border-aeblack-100 p-3 text-start hover:bg-aeblack-50"
            >
              <div className="min-w-0 flex-1">
                <div className="font-mono text-xs font-bold">{ticket.reference}</div>
                <div className="truncate text-sm font-semibold">{l(ticket.serviceName)}</div>
                <div className="mt-1 text-[10px] text-ink-faint">
                  {ticket.assignment?.stage ?? ticket.state}
                  {ticket.assignment?.assigneeName ? ` · ${ticket.assignment.assigneeName}` : ''}
                </div>
              </div>
              <StatusPill state={ticket.state} label={ticket.state.replaceAll('_', ' ')} />
            </button>
          ))}
          {activeTickets.length === 0 && <EmptyState title={l({ en: 'No active tickets', ar: 'لا توجد تذاكر نشطة' })} />}
        </div>
      </GlassCard>

      <GlassCard className="p-5">
        <SectionTitle
          icon="conversation"
          title={l({ en: 'Recent conversations', ar: 'أحدث المحادثات' })}
          count={customer.conversations.length}
          action={<GlassButton size="sm" variant="ghost" onClick={() => onTab('conversations')}>{l({ en: 'View all', ar: 'عرض الكل' })}</GlassButton>}
        />
        <div className="space-y-2">
          {customer.conversations.slice(0, 5).map((conversation) => (
            <button
              key={conversation.id}
              type="button"
              onClick={() => onOpenConversation(conversation.id)}
              className="flex min-h-14 w-full items-center gap-3 rounded-lg border border-aeblack-100 p-3 text-start hover:bg-aeblack-50"
            >
              <span className="grid h-9 w-9 shrink-0 place-items-center rounded-full bg-seablue-50 text-oasis">
                <CrmIcon name="conversation" />
              </span>
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-2 text-xs font-bold">
                  <span>{conversation.channel}</span>
                  <span className="font-mono uppercase text-ink-faint">{conversation.lang}</span>
                  {conversation.applicationReference && <span>{conversation.applicationReference}</span>}
                  {conversation.cxone && (
                    <CxoneBadge
                      status={conversation.cxone.status}
                      className="!min-h-6 !py-0"
                    />
                  )}
                </div>
                <div className="mt-1 truncate text-xs text-ink-soft">
                  {conversation.lastMessage?.preview ?? l({ en: 'No messages yet', ar: 'لا توجد رسائل بعد' })}
                </div>
              </div>
              <div className="text-end text-[10px] text-ink-faint">
                <div>{conversation.messageCount} {l(S.messages)}</div>
                {conversation.lastMessageAt && <div>{formatDate(conversation.lastMessageAt, lang)}</div>}
              </div>
            </button>
          ))}
          {customer.conversations.length === 0 && <EmptyState title={l({ en: 'No conversations', ar: 'لا توجد محادثات' })} />}
        </div>
      </GlassCard>

      <GlassCard className="p-5">
        <SectionTitle
          icon="note"
          title={l({ en: 'Pinned and recent notes', ar: 'الملاحظات المثبتة والحديثة' })}
          count={customer.notes.length}
          action={<GlassButton size="sm" variant="ghost" onClick={() => onTab('notes')}>{l({ en: 'Add note', ar: 'إضافة ملاحظة' })}</GlassButton>}
        />
        <div className="space-y-2">
          {(pinnedNotes.length ? pinnedNotes : customer.notes).slice(0, 4).map((note) => (
            <NoteCard key={note.id} note={note} compact />
          ))}
          {customer.notes.length === 0 && <EmptyState title={l({ en: 'No internal notes', ar: 'لا توجد ملاحظات داخلية' })} />}
        </div>
      </GlassCard>

      <GlassCard className="p-5">
        <SectionTitle
          icon="task"
          title={l({ en: 'Open follow-ups', ar: 'المتابعات المفتوحة' })}
          count={openTasks.length}
          action={<GlassButton size="sm" variant="ghost" onClick={() => onTab('tasks')}>{l({ en: 'Manage', ar: 'إدارة' })}</GlassButton>}
        />
        <div className="space-y-2">
          {openTasks.slice(0, 5).map((task) => <TaskSummary key={task.id} task={task} />)}
          {openTasks.length === 0 && <EmptyState title={l({ en: 'No open tasks', ar: 'لا توجد مهام مفتوحة' })} />}
        </div>
      </GlassCard>
    </div>
  );
}

/* --------------------------------------------------------------------------
 * RelationshipTimelineCard — unified chronological feed of applications
 * (created/submitted/decision), conversations, notes and tasks, built from
 * the page's existing customer 360 payload (plan point 36).
 * ------------------------------------------------------------------------- */
function RelationshipTimelineCard({ customer }: { customer: Customer360Record }) {
  const { l, lang } = useI18n();
  const [expanded, setExpanded] = useState(false);

  interface TimelineEvent {
    at: string;
    icon: string;
    label: string;
    detail: string;
    tone: string;
  }
  const events: TimelineEvent[] = [];
  for (const app of customer.applications) {
    const ref = `${app.reference} · ${l(app.serviceName)}`;
    events.push({ at: app.createdAt, icon: 'ticket', label: l(S.tlApplicationCreated), detail: ref, tone: SECTION_ACCENTS.assurance });
    if (app.submittedAt) {
      events.push({ at: app.submittedAt, icon: 'ticket', label: l(S.tlApplicationSubmitted), detail: ref, tone: SECTION_ACCENTS.assurance });
    }
    if (app.decision) {
      events.push({ at: app.closedAt ?? app.updatedAt, icon: 'consent', label: l(S.tlApplicationDecision), detail: ref, tone: SECTION_ACCENTS.engagement });
    }
  }
  for (const conversation of customer.conversations) {
    events.push({
      at: conversation.createdAt,
      icon: 'conversation',
      label: l(S.tlConversation),
      detail: `${conversation.channel} · ${conversation.messageCount} ${l(S.messages)}`,
      tone: SECTION_ACCENTS.operations,
    });
  }
  for (const note of customer.notes) {
    events.push({
      at: note.createdAt,
      icon: 'note',
      label: l(S.tlNote),
      detail: `${note.authorName}: ${note.body.slice(0, 90)}`,
      tone: SECTION_ACCENTS.platform,
    });
  }
  for (const task of customer.tasks) {
    events.push({ at: task.createdAt, icon: 'task', label: l(S.tlTaskCreated), detail: task.title, tone: SECTION_ACCENTS.money });
    if (task.completedAt) {
      events.push({ at: task.completedAt, icon: 'task', label: l(S.tlTaskCompleted), detail: task.title, tone: SECTION_ACCENTS.engagement });
    }
  }
  events.sort((a, b) => Date.parse(b.at) - Date.parse(a.at));
  const visible = expanded ? events.slice(0, 30) : events.slice(0, 8);

  return (
    <SectionCard
      className="xl:col-span-2"
      accent={SECTION_ACCENTS.engagement}
      title={l(S.relationshipTimeline)}
      hint={l(S.relationshipTimelineHint)}
      actions={<span className="aegov-badge">{events.length}</span>}
    >
      <div className="space-y-2.5">
        {visible.map((event, index) => (
          <div key={`${event.at}-${index}`} className="flex items-start gap-3">
            <span
              aria-hidden
              className="grid h-7 w-7 shrink-0 place-items-center rounded-lg"
              style={{ backgroundColor: `${event.tone}1a`, color: event.tone }}
            >
              <CrmIcon name={event.icon} className="h-3.5 w-3.5" />
            </span>
            <div className="min-w-0 flex-1">
              <div className="flex flex-wrap items-baseline gap-x-2">
                <span className="text-xs font-extrabold">{event.label}</span>
                <time className="ms-auto shrink-0 text-[10px] text-ink-faint">{formatDate(event.at, lang)}</time>
              </div>
              <div className="mt-0.5 truncate text-xs text-ink-soft" title={event.detail}>{event.detail}</div>
            </div>
          </div>
        ))}
        {events.length === 0 && <EmptyState title={l(S.crmTimelineEmpty)} />}
      </div>
      {events.length > 8 && (
        <div className="mt-3 flex items-center gap-3 border-t border-aeblack-100 pt-3">
          <GlassButton size="sm" variant="ghost" onClick={() => setExpanded((value) => !value)}>
            {expanded ? l(S.showLess) : l(S.showMore)}
          </GlassButton>
          {expanded && events.length > 30 && (
            <span className="text-[10px] text-ink-faint">+{events.length - 30} {l(S.olderEvents)}</span>
          )}
        </div>
      )}
    </SectionCard>
  );
}

function TicketsTab({
  customer,
  onOpen,
}: {
  customer: Customer360Record;
  onOpen: (reference: string) => void;
}) {
  const { l, lang } = useI18n();
  const [state, setState] = useState('');
  const rows = customer.applications.filter((app) => !state || app.state === state);
  const states = [...new Set(customer.applications.map((app) => app.state))].sort();
  const columns: DataTableColumn<Customer360Record['applications'][number]>[] = [
    {
      id: 'ticket',
      header: l({ en: 'Ticket', ar: 'التذكرة' }),
      sortable: true,
      value: (row) => row.reference,
      render: (row) => <div><div className="font-mono text-xs font-bold">{row.reference}</div><div className="mt-0.5 max-w-72 truncate font-semibold">{l(row.serviceName)}</div></div>,
    },
    { id: 'state', header: l(S.status), sortable: true, value: (row) => row.state, render: (row) => <StatusPill state={row.state} label={row.state.replaceAll('_', ' ')} /> },
    { id: 'stage', header: l(S.stage), sortable: true, value: (row) => row.assignment?.stage ?? '', render: (row) => <div><div className="font-semibold">{row.assignment?.stage ?? '—'}</div><div className="text-[10px] text-ink-faint">{row.assignment?.status ?? ''}</div></div> },
    { id: 'owner', header: l({ en: 'Assignee', ar: 'المكلّف' }), sortable: true, value: (row) => row.assignment?.assigneeName ?? '', render: (row) => <span>{row.assignment?.assigneeName ?? l({ en: 'Unassigned', ar: 'غير معيّن' })}</span> },
    { id: 'sla', header: l({ en: 'SLA / updated', ar: 'الخدمة / التحديث' }), sortable: true, value: (row) => row.slaDueAt ?? row.updatedAt, render: (row) => <div className="text-xs"><div>{row.slaDueAt ? formatDate(row.slaDueAt, lang) : '—'}</div><div className="text-[10px] text-ink-faint">{formatDate(row.updatedAt, lang)}</div></div> },
    { id: 'channel', header: l(S.channel), sortable: true, value: (row) => row.channel, render: (row) => <span className="font-mono text-[10px] font-bold uppercase">{row.channel} · {row.lang}</span> },
    { id: 'events', header: l({ en: 'Events', ar: 'الأحداث' }), sortable: true, value: (row) => row.eventCount, render: (row) => <span className="font-mono font-bold">{row.eventCount}</span> },
    { id: 'action', header: '', align: 'end', render: () => <span className="text-xs font-bold text-gold-deep">{l({ en: 'Open case', ar: 'فتح الحالة' })} →</span> },
  ];
  return (
    <GlassCard className="p-5">
      <SectionTitle icon="ticket" title={l({ en: 'All tickets and applications', ar: 'جميع التذاكر والطلبات' })} count={customer.applications.length} />
      <FilterBar summary={`${rows.length} ${l(S.rows)}`}>
        <GlassSelect value={state} onChange={(event) => setState(event.target.value)}>
          <option value="">{l({ en: 'All ticket states', ar: 'كل حالات التذاكر' })}</option>
          {states.map((value) => <option key={value}>{value}</option>)}
        </GlassSelect>
      </FilterBar>
      <DataTable
        rows={rows}
        columns={columns}
        rowKey={(row) => row.reference}
        emptyTitle={l({ en: 'No tickets in this view', ar: 'لا توجد تذاكر في هذا العرض' })}
        filename={`customer-${customer.id}-tickets.csv`}
        onRowClick={(row) => onOpen(row.reference)}
      />
    </GlassCard>
  );
}

function ConversationsTab({
  customer,
  onOpen,
}: {
  customer: Customer360Record;
  onOpen: (id: string) => void;
}) {
  const { l, lang } = useI18n();
  const [channel, setChannel] = useState('');
  const rows = customer.conversations.filter((conversation) => !channel || conversation.channel === channel);
  const channels = [...new Set(customer.conversations.map((conversation) => conversation.channel))].sort();
  const columns: DataTableColumn<Customer360Record['conversations'][number]>[] = [
    { id: 'conversation', header: l({ en: 'Conversation', ar: 'المحادثة' }), sortable: true, value: (row) => row.id, render: (row) => <div><div className="font-mono text-xs font-bold">{row.id.slice(0, 12)}</div><div className="mt-0.5 text-xs text-ink-faint">{row.applicationReference ?? l({ en: 'General enquiry', ar: 'استفسار عام' })}</div></div> },
    { id: 'channel', header: l(S.channel), sortable: true, value: (row) => row.channel, render: (row) => <div><span className="font-semibold">{row.channel}</span><span className="ms-2 font-mono text-[10px] uppercase text-ink-faint">{row.lang}</span></div> },
    { id: 'handover', header: l({ en: 'Ownership', ar: 'ملكية المحادثة' }), sortable: true, value: (row) => row.handoverState, render: (row) => <StatusPill state={row.handoverState === 'ai' ? 'published' : 'in_progress'} label={row.handoverState === 'ai' ? 'AI' : row.handoverState.replaceAll('_', ' ')} /> },
    {
      id: 'cxone',
      header: 'NiCE CXone',
      sortable: true,
      value: (row) => row.cxone?.status ?? '',
      render: (row) =>
        row.cxone ? (
          <div>
            <CxoneBadge status={row.cxone.status} />
            <div className="mt-1 max-w-48 truncate text-[9px] text-ink-faint">
              {row.cxone.assigneeName ??
                row.cxone.routingQueueName ??
                row.cxone.skillId ??
                row.cxone.contactId}
            </div>
          </div>
        ) : (
          <span className="text-[10px] text-ink-faint">—</span>
        ),
    },
    { id: 'messages', header: l(S.messages), sortable: true, value: (row) => row.messageCount, render: (row) => <span className="font-mono font-bold">{row.messageCount}</span> },
    { id: 'preview', header: l({ en: 'Latest message', ar: 'آخر رسالة' }), value: (row) => row.lastMessage?.preview, render: (row) => <div className="max-w-md"><div className="truncate text-xs">{row.lastMessage?.preview ?? '—'}</div><div className="text-[10px] text-ink-faint">{row.lastMessage?.role ?? ''}</div></div> },
    { id: 'last', header: l({ en: 'Last activity', ar: 'آخر نشاط' }), sortable: true, value: (row) => row.lastMessageAt, render: (row) => <span className="text-xs">{row.lastMessageAt ? formatDate(row.lastMessageAt, lang) : formatDate(row.createdAt, lang)}</span> },
    { id: 'action', header: '', align: 'end', render: () => <span className="text-xs font-bold text-gold-deep">{l({ en: 'Open transcript', ar: 'فتح المحادثة' })} →</span> },
  ];
  return (
    <GlassCard className="p-5">
      <SectionTitle icon="conversation" title={l({ en: 'Conversation history', ar: 'سجل المحادثات' })} count={customer.conversations.length} />
      <FilterBar summary={`${rows.length} ${l(S.rows)}`}>
        <GlassSelect value={channel} onChange={(event) => setChannel(event.target.value)}>
          <option value="">{l({ en: 'All channels', ar: 'كل القنوات' })}</option>
          {channels.map((value) => <option key={value}>{value}</option>)}
        </GlassSelect>
      </FilterBar>
      <DataTable
        rows={rows}
        columns={columns}
        rowKey={(row) => row.id}
        emptyTitle={l({ en: 'No conversations', ar: 'لا توجد محادثات' })}
        filename={`customer-${customer.id}-conversations.csv`}
        onRowClick={(row) => onOpen(row.id)}
      />
    </GlassCard>
  );
}

function ActivityTab({ customer }: { customer: Customer360Record }) {
  const { l, lang } = useI18n();
  const [type, setType] = useState('');
  const types = [...new Set(customer.timeline.map((entry) => entry.type))].sort();
  const entries = customer.timeline.filter((entry) => !type || entry.type === type);
  return (
    <GlassCard className="p-5">
      <SectionTitle icon="activity" title={l({ en: 'Complete interaction timeline', ar: 'الخط الزمني الكامل للتفاعل' })} count={entries.length} />
      <FilterBar>
        <GlassSelect value={type} onChange={(event) => setType(event.target.value)}>
          <option value="">{l({ en: 'All activity types', ar: 'كل أنواع النشاط' })}</option>
          {types.map((value) => <option key={value}>{value.replaceAll('_', ' ')}</option>)}
        </GlassSelect>
      </FilterBar>
      <div className="relative ms-4 border-s border-aeblack-200 ps-6">
        {entries.map((entry, index) => (
          <div key={`${entry.at}-${entry.type}-${index}`} className="relative pb-5">
            <span className="absolute -start-[2.15rem] top-0.5 grid h-5 w-5 place-items-center rounded-full border border-aeblack-200 bg-white text-ink-faint">
              <CrmIcon name={activityIcon(entry.type)} className="h-3 w-3" />
            </span>
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-xs font-extrabold">{entry.type.replaceAll('_', ' ')}</span>
              {entry.channel && <span className="aegov-badge">{entry.channel}</span>}
              <time className="ms-auto text-[10px] text-ink-faint">{formatDate(entry.at, lang)}</time>
            </div>
            <div className="mt-1 text-sm leading-relaxed text-ink-soft">{timelineText(entry, l)}</div>
          </div>
        ))}
        {entries.length === 0 && <EmptyState title={l({ en: 'No activity in this view', ar: 'لا يوجد نشاط في هذا العرض' })} />}
      </div>
    </GlassCard>
  );
}

function activityIcon(type: string): string {
  if (type === 'message') return 'conversation';
  if (type === 'application_event') return 'ticket';
  if (type === 'notification') return 'consent';
  if (type === 'crm_note') return 'note';
  if (type === 'crm_task') return 'task';
  if (type === 'complaint') return 'ticket';
  if (type === 'satisfaction') return 'consent';
  if (type === 'payment') return 'document';
  return 'activity';
}

function timelineText(
  entry: Customer360Record['timeline'][number],
  localize: (value: Localized | undefined | null) => string,
): string {
  if (entry.type === 'message') {
    return String(entry.detail.text ?? entry.detail.direction ?? localize({ en: 'Message', ar: 'رسالة' })).slice(0, 260);
  }
  if (entry.type === 'application_event') {
    return `${String(entry.detail.event ?? 'Application event')} · ${String(entry.detail.reference ?? '')}`;
  }
  if (entry.type === 'notification') {
    return `${String(entry.detail.template ?? 'Notification')} · ${String(entry.detail.status ?? '')}`;
  }
  if (entry.type === 'crm_note') {
    return `${String(entry.detail.author ?? '')}: ${String(entry.detail.body ?? '')}`;
  }
  if (entry.type === 'crm_task') {
    return `${String(entry.detail.title ?? '')} · ${String(entry.detail.status ?? '')}`;
  }
  if (entry.type === 'complaint') {
    return `${String(entry.detail.category ?? 'Complaint')} · ${String(entry.detail.status ?? '')} · ${String(entry.detail.body ?? '')}`;
  }
  if (entry.type === 'satisfaction') {
    return `${String(entry.detail.score ?? '—')}/5 · ${String(entry.detail.comment ?? '')}`;
  }
  if (entry.type === 'payment') {
    return `${String(entry.detail.status ?? 'Payment')} · ${Number(entry.detail.amountFils ?? 0) / 100} ${String(entry.detail.currency ?? 'AED')}`;
  }
  return JSON.stringify(entry.detail);
}

function NotesTab({
  customer,
  busy,
  onCreate,
  onPin,
}: {
  customer: Customer360Record;
  busy: boolean;
  onCreate: (input: { body: string; kind: CustomerCrmNote['kind']; tags?: string[]; pinned?: boolean }) => Promise<void>;
  onPin: (note: CustomerCrmNote) => Promise<void>;
}) {
  const { l } = useI18n();
  const [body, setBody] = useState('');
  const [kind, setKind] = useState<CustomerCrmNote['kind']>('comment');
  const [tags, setTags] = useState('');
  const [pinned, setPinned] = useState(false);
  const submit = async () => {
    if (!body.trim()) return;
    await onCreate({
      body: body.trim(),
      kind,
      tags: tags.split(',').map((tag) => tag.trim()).filter(Boolean),
      pinned,
    });
    setBody('');
    setTags('');
    setPinned(false);
  };
  return (
    <div className="grid items-start gap-4 xl:grid-cols-[minmax(22rem,0.75fr)_minmax(0,1.25fr)]">
      <GlassCard className="sticky top-[10rem] p-5">
        <SectionTitle icon="note" title={l({ en: 'Add internal note', ar: 'إضافة ملاحظة داخلية' })} />
        <label>
          <span className="mb-1 block text-xs font-bold">{l({ en: 'Note type', ar: 'نوع الملاحظة' })}</span>
          <GlassSelect value={kind} onChange={(event) => setKind(event.target.value as CustomerCrmNote['kind'])}>
            <option value="comment">{l({ en: 'General comment', ar: 'تعليق عام' })}</option>
            <option value="call_note">{l({ en: 'Call note', ar: 'ملاحظة مكالمة' })}</option>
            <option value="case_note">{l({ en: 'Case note', ar: 'ملاحظة حالة' })}</option>
            <option value="follow_up">{l({ en: 'Follow-up', ar: 'متابعة' })}</option>
          </GlassSelect>
        </label>
        <label className="mt-3 block">
          <span className="mb-1 block text-xs font-bold">{l({ en: 'Comment', ar: 'التعليق' })}</span>
          <GlassTextarea value={body} onChange={(event) => setBody(event.target.value)} placeholder={l({ en: 'Record context, outcome, or next step…', ar: 'سجّل السياق أو النتيجة أو الخطوة التالية…' })} className="[&_textarea]:min-h-32" />
        </label>
        <label className="mt-3 block">
          <span className="mb-1 block text-xs font-bold">{l({ en: 'Tags', ar: 'الوسوم' })}</span>
          <GlassInput value={tags} onChange={(event) => setTags(event.target.value)} placeholder={l({ en: 'complaint, priority, renewal', ar: 'شكوى، أولوية، تجديد' })} />
        </label>
        <label className="mt-3 flex min-h-10 items-center gap-2 text-xs font-semibold">
          <input type="checkbox" checked={pinned} onChange={(event) => setPinned(event.target.checked)} className="h-4 w-4" />
          {l({ en: 'Pin to customer overview', ar: 'تثبيت في نظرة المتعامل' })}
        </label>
        <GlassButton variant="primary" className="mt-3 w-full" disabled={busy || !body.trim()} onClick={() => void submit()}>
          {l({ en: 'Add audited note', ar: 'إضافة ملاحظة مدققة' })}
        </GlassButton>
      </GlassCard>

      <GlassCard className="p-5">
        <SectionTitle icon="note" title={l({ en: 'Internal notes', ar: 'الملاحظات الداخلية' })} count={customer.notes.length} />
        <QuickNoteComposer busy={busy} onCreate={onCreate} />
        <div className="space-y-3">
          {customer.notes.map((note) => (
            <NoteCard key={note.id} note={note} onPin={() => void onPin(note)} />
          ))}
          {customer.notes.length === 0 && <EmptyState title={l({ en: 'No notes yet', ar: 'لا توجد ملاحظات بعد' })} />}
        </div>
      </GlassCard>
    </div>
  );
}

/* --------------------------------------------------------------------------
 * QuickNoteComposer — fast note entry above the notes list with bilingual
 * one-tap templates; saves through the existing add-note mutation and clears
 * with a subtle confirmation (plan point 37).
 * ------------------------------------------------------------------------- */
function QuickNoteComposer({
  busy,
  onCreate,
}: {
  busy: boolean;
  onCreate: (input: { body: string; kind: CustomerCrmNote['kind']; tags?: string[]; pinned?: boolean }) => Promise<void>;
}) {
  const { l } = useI18n();
  const [body, setBody] = useState('');
  const [saved, setSaved] = useState(false);
  const templates: { label: Localized; body: Localized }[] = [
    { label: S.tplCalled, body: S.tplCalledBody },
    { label: S.tplDocs, body: S.tplDocsBody },
    { label: S.tplFollowUp, body: S.tplFollowUpBody },
  ];
  const save = async () => {
    const text = body.trim();
    if (!text) return;
    await onCreate({ body: text, kind: 'comment' });
    setBody('');
    setSaved(true);
    window.setTimeout(() => setSaved(false), 2500);
  };
  return (
    <div className="mb-4 rounded-xl border border-aeblack-100 bg-aeblack-50/60 p-3">
      <div className="mb-2 flex flex-wrap items-center gap-1.5">
        <span className="me-1 text-[10px] font-extrabold uppercase tracking-wide text-ink-faint">
          {l(S.quickNote)}
        </span>
        {templates.map((template) => (
          <button
            key={template.label.en}
            type="button"
            onClick={() =>
              setBody((prev) => (prev.trim() ? `${prev.trim()}\n${l(template.body)}` : l(template.body)))
            }
            className="min-h-7 cursor-pointer rounded-full border border-aeblack-200 bg-white px-2.5 text-[10px] font-bold text-ink-soft transition hover:border-aegold-300 hover:text-ink"
          >
            {l(template.label)}
          </button>
        ))}
      </div>
      <GlassTextarea
        value={body}
        onChange={(event) => setBody(event.target.value)}
        placeholder={l(S.quickNotePlaceholder)}
        className="[&_textarea]:min-h-20 [&_textarea]:bg-white"
      />
      <div className="mt-2 flex items-center gap-2">
        <GlassButton size="sm" variant="primary" disabled={busy || !body.trim()} onClick={() => void save()}>
          {l(S.saveNote)}
        </GlassButton>
        {saved && (
          <span role="status" className="text-[10px] font-bold text-aegreen-700">
            ✓ {l(S.noteSaved)}
          </span>
        )}
      </div>
    </div>
  );
}

function NoteCard({
  note,
  compact,
  onPin,
}: {
  note: CustomerCrmNote;
  compact?: boolean;
  onPin?: () => void;
}) {
  const { lang, l } = useI18n();
  return (
    <article className={`rounded-lg border p-3 ${note.pinned ? 'border-aegold-300 bg-aegold-50/50' : 'border-aeblack-100 bg-white'}`}>
      <div className="flex flex-wrap items-center gap-2">
        {note.pinned && <span className="text-gold-deep"><CrmIcon name="pin" /></span>}
        <span className="text-xs font-extrabold">{note.authorName}</span>
        <span className="aegov-badge">{note.kind.replaceAll('_', ' ')}</span>
        <time className="ms-auto text-[10px] text-ink-faint">{formatDate(note.createdAt, lang)}</time>
      </div>
      <p className={`mt-2 whitespace-pre-wrap text-sm leading-relaxed text-ink-soft ${compact ? 'line-clamp-3' : ''}`}>{note.body}</p>
      <div className="mt-2 flex flex-wrap items-center gap-1">
        {note.tags.map((tag) => <span key={tag} className="rounded bg-aeblack-50 px-1.5 py-0.5 text-[10px] font-semibold text-ink-faint">{tag}</span>)}
        {onPin && (
          <button type="button" onClick={onPin} className="ms-auto inline-flex min-h-8 items-center gap-1 rounded px-2 text-[10px] font-bold text-gold-deep hover:bg-aegold-50">
            <CrmIcon name="pin" className="h-3.5 w-3.5" />
            {note.pinned ? l({ en: 'Unpin', ar: 'إلغاء التثبيت' }) : l({ en: 'Pin', ar: 'تثبيت' })}
          </button>
        )}
      </div>
    </article>
  );
}

function TasksTab({
  customer,
  busy,
  onCreate,
  onStatus,
}: {
  customer: Customer360Record;
  busy: boolean;
  onCreate: (input: { title: string; description?: string; priority: CustomerCrmTask['priority']; assignedTo?: string; dueAt?: string }) => Promise<void>;
  onStatus: (task: CustomerCrmTask, status: CustomerCrmTask['status']) => Promise<void>;
}) {
  const { l } = useI18n();
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [priority, setPriority] = useState<CustomerCrmTask['priority']>('normal');
  const [assignedTo, setAssignedTo] = useState('');
  const [dueAt, setDueAt] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const rows = customer.tasks.filter((task) => !statusFilter || task.status === statusFilter);
  const submit = async () => {
    if (!title.trim()) return;
    await onCreate({
      title: title.trim(),
      description: description.trim() || undefined,
      priority,
      assignedTo: assignedTo || undefined,
      dueAt: dueAt || undefined,
    });
    setTitle('');
    setDescription('');
    setDueAt('');
  };
  return (
    <div className="grid items-start gap-4 xl:grid-cols-[minmax(22rem,0.75fr)_minmax(0,1.25fr)]">
      <GlassCard className="sticky top-[10rem] p-5">
        <SectionTitle icon="task" title={l({ en: 'Create follow-up task', ar: 'إنشاء مهمة متابعة' })} />
        <label><span className="mb-1 block text-xs font-bold">{l({ en: 'Task title', ar: 'عنوان المهمة' })}</span><GlassInput value={title} onChange={(event) => setTitle(event.target.value)} /></label>
        <label className="mt-3 block"><span className="mb-1 block text-xs font-bold">{l({ en: 'Description', ar: 'الوصف' })}</span><GlassTextarea value={description} onChange={(event) => setDescription(event.target.value)} className="[&_textarea]:min-h-24" /></label>
        <div className="mt-3 grid gap-3 sm:grid-cols-2">
          <label><span className="mb-1 block text-xs font-bold">{l({ en: 'Priority', ar: 'الأولوية' })}</span><GlassSelect value={priority} onChange={(event) => setPriority(event.target.value as CustomerCrmTask['priority'])}><option value="low">low</option><option value="normal">normal</option><option value="high">high</option><option value="urgent">urgent</option></GlassSelect></label>
          <label><span className="mb-1 block text-xs font-bold">{l({ en: 'Due date', ar: 'تاريخ الاستحقاق' })}</span><GlassInput type="datetime-local" value={dueAt} onChange={(event) => setDueAt(event.target.value)} /></label>
        </div>
        <label className="mt-3 block"><span className="mb-1 block text-xs font-bold">{l({ en: 'Assign to', ar: 'تعيين إلى' })}</span><GlassSelect value={assignedTo} onChange={(event) => setAssignedTo(event.target.value)}><option value="">{l({ en: 'Assign to me', ar: 'تعيين لي' })}</option>{customer.staffOptions.map((staff) => <option key={staff.id} value={staff.id}>{staff.displayName}</option>)}</GlassSelect></label>
        <GlassButton variant="primary" className="mt-4 w-full" disabled={busy || !title.trim()} onClick={() => void submit()}>{l({ en: 'Create task', ar: 'إنشاء المهمة' })}</GlassButton>
      </GlassCard>

      <GlassCard className="p-5">
        <SectionTitle icon="task" title={l({ en: 'Customer tasks', ar: 'مهام المتعامل' })} count={customer.tasks.length} />
        <FilterBar>
          <GlassSelect value={statusFilter} onChange={(event) => setStatusFilter(event.target.value)}>
            <option value="">{l({ en: 'All task statuses', ar: 'كل حالات المهام' })}</option>
            <option value="open">open</option>
            <option value="in_progress">in progress</option>
            <option value="completed">completed</option>
            <option value="cancelled">cancelled</option>
          </GlassSelect>
        </FilterBar>
        <div className="space-y-3">
          {rows.map((task) => (
            <TaskCard key={task.id} task={task} busy={busy} onStatus={(status) => void onStatus(task, status)} />
          ))}
          {rows.length === 0 && <EmptyState title={l({ en: 'No tasks in this view', ar: 'لا توجد مهام في هذا العرض' })} />}
        </div>
      </GlassCard>
    </div>
  );
}

function TaskSummary({ task }: { task: CustomerCrmTask }) {
  const { lang } = useI18n();
  return (
    <div className="flex items-center gap-3 rounded-lg border border-aeblack-100 p-3">
      <span className={`h-2.5 w-2.5 rounded-full ${task.priority === 'urgent' ? 'bg-reject' : task.priority === 'high' ? 'bg-review' : 'bg-oasis'}`} />
      <div className="min-w-0 flex-1">
        <div className="truncate text-sm font-bold">{task.title}</div>
        <div className="text-[10px] text-ink-faint">{task.assignedToName ?? task.createdByName}{task.dueAt ? ` · ${formatDate(task.dueAt, lang)}` : ''}</div>
      </div>
      <StatusPill state={task.status === 'completed' ? 'published' : 'in_progress'} label={task.status.replaceAll('_', ' ')} />
    </div>
  );
}

function TaskCard({
  task,
  busy,
  onStatus,
}: {
  task: CustomerCrmTask;
  busy: boolean;
  onStatus: (status: CustomerCrmTask['status']) => void;
}) {
  const { l, lang } = useI18n();
  return (
    <article className="rounded-lg border border-aeblack-100 p-4">
      <div className="flex flex-wrap items-start gap-3">
        <span className={`mt-1.5 h-3 w-3 rounded-full ${task.priority === 'urgent' ? 'bg-reject' : task.priority === 'high' ? 'bg-review' : task.priority === 'low' ? 'bg-aeblack-200' : 'bg-oasis'}`} />
        <div className="min-w-0 flex-1">
          <div className="font-extrabold">{task.title}</div>
          {task.description && <p className="mt-1 whitespace-pre-wrap text-sm text-ink-soft">{task.description}</p>}
          <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-[10px] text-ink-faint">
            <span>{l({ en: 'Assigned', ar: 'مكلّف' })}: {task.assignedToName ?? '—'}</span>
            <span>{l({ en: 'Created by', ar: 'أنشأها' })}: {task.createdByName}</span>
            {task.dueAt && <span>{l(S.due)}: {formatDate(task.dueAt, lang)}</span>}
          </div>
        </div>
        <StatusPill state={task.status === 'completed' ? 'published' : task.status === 'cancelled' ? 'rejected' : 'in_progress'} label={task.status.replaceAll('_', ' ')} />
      </div>
      <div className="mt-3 flex flex-wrap gap-2 border-t border-aeblack-100 pt-3">
        {task.status === 'open' && <GlassButton size="sm" disabled={busy} onClick={() => onStatus('in_progress')}>{l({ en: 'Start', ar: 'بدء' })}</GlassButton>}
        {(task.status === 'open' || task.status === 'in_progress') && <GlassButton size="sm" variant="primary" disabled={busy} onClick={() => onStatus('completed')}>{l({ en: 'Complete', ar: 'إكمال' })}</GlassButton>}
        {!['completed', 'cancelled'].includes(task.status) && <GlassButton size="sm" variant="ghost" disabled={busy} onClick={() => onStatus('cancelled')}>{l({ en: 'Cancel', ar: 'إلغاء' })}</GlassButton>}
        {(task.status === 'completed' || task.status === 'cancelled') && <GlassButton size="sm" variant="ghost" disabled={busy} onClick={() => onStatus('open')}>{l({ en: 'Reopen', ar: 'إعادة الفتح' })}</GlassButton>}
      </div>
    </article>
  );
}

function FeedbackTab({ customer }: { customer: Customer360Record }) {
  const { l, lang } = useI18n();
  const openComplaints = customer.complaints.filter(
    (complaint) => complaint.status === 'open' || complaint.status === 'escalated',
  );
  const averageScore =
    customer.satisfaction.length > 0
      ? customer.satisfaction.reduce((sum, survey) => sum + survey.score, 0) /
        customer.satisfaction.length
      : null;
  return (
    <div className="space-y-4">
      <div className="grid gap-3 sm:grid-cols-3">
        <MetricCard
          label={l({ en: 'Open complaints', ar: 'الشكاوى المفتوحة' })}
          value={openComplaints.length}
          tone={openComplaints.length ? 'danger' : 'success'}
        />
        <MetricCard
          label={l({ en: 'Average satisfaction', ar: 'متوسط الرضا' })}
          value={averageScore === null ? '—' : `${averageScore.toFixed(1)} / 5`}
          tone={averageScore !== null && averageScore < 3 ? 'danger' : 'success'}
        />
        <MetricCard
          label={l({ en: 'Feedback responses', ar: 'استجابات الرضا' })}
          value={customer.satisfaction.length}
        />
      </div>
      <div className="grid gap-4 xl:grid-cols-2">
        <GlassCard className="p-5">
          <SectionTitle
            icon="ticket"
            title={l({ en: 'Complaints', ar: 'الشكاوى' })}
            count={customer.complaints.length}
          />
          <div className="space-y-3">
            {customer.complaints.map((complaint) => (
              <article
                key={complaint.id}
                className="rounded-lg border border-aeblack-100 p-4"
              >
                <div className="flex flex-wrap items-center gap-2">
                  <StatusPill
                    state={
                      complaint.status === 'closed' || complaint.status === 'auto_resolved'
                        ? 'published'
                        : complaint.status === 'escalated'
                          ? 'rejected'
                          : 'in_review'
                    }
                    label={complaint.status.replaceAll('_', ' ')}
                  />
                  <span className="aegov-badge">{complaint.category ?? 'other'}</span>
                  <span
                    className={`aegov-badge ${
                      complaint.severity === 'high'
                        ? 'badge-error'
                        : complaint.severity === 'medium'
                          ? 'badge-warning'
                          : 'badge-info'
                    }`}
                  >
                    {complaint.severity}
                  </span>
                  <time className="ms-auto text-[10px] text-ink-faint">
                    {formatDate(complaint.createdAt, lang)}
                  </time>
                </div>
                <p className="mt-2 whitespace-pre-wrap text-sm leading-relaxed text-ink-soft">
                  {complaint.body ?? l({ en: 'No complaint text', ar: 'لا يوجد نص للشكوى' })}
                </p>
                <div className="mt-2 flex flex-wrap gap-3 text-[10px] text-ink-faint">
                  {complaint.applicationReference && <span>{complaint.applicationReference}</span>}
                  {complaint.serviceSlug && <span>{complaint.serviceSlug}</span>}
                  {complaint.channel && <span>{complaint.channel}</span>}
                  {complaint.sentiment && <span>{complaint.sentiment}</span>}
                </div>
              </article>
            ))}
            {customer.complaints.length === 0 && (
              <EmptyState title={l({ en: 'No complaints', ar: 'لا توجد شكاوى' })} />
            )}
          </div>
        </GlassCard>

        <GlassCard className="p-5">
          <SectionTitle
            icon="consent"
            title={l({ en: 'Satisfaction history', ar: 'سجل الرضا' })}
            count={customer.satisfaction.length}
          />
          <div className="space-y-3">
            {customer.satisfaction.map((survey) => (
              <article key={survey.id} className="rounded-lg border border-aeblack-100 p-4">
                <div className="flex items-center gap-3">
                  <span className="font-mono text-2xl font-black text-gold-deep">
                    {survey.score}/5
                  </span>
                  <div className="min-w-0 flex-1">
                    <div className="truncate text-sm font-bold">
                      {survey.serviceSlug ?? l({ en: 'General experience', ar: 'التجربة العامة' })}
                    </div>
                    <div className="text-[10px] text-ink-faint">
                      {survey.applicationReference ?? survey.channel ?? '—'}
                    </div>
                  </div>
                  <time className="text-[10px] text-ink-faint">
                    {formatDate(survey.createdAt, lang)}
                  </time>
                </div>
                {survey.comment && (
                  <p className="mt-2 text-sm leading-relaxed text-ink-soft">{survey.comment}</p>
                )}
              </article>
            ))}
            {customer.satisfaction.length === 0 && (
              <EmptyState title={l({ en: 'No satisfaction responses', ar: 'لا توجد استجابات رضا' })} />
            )}
          </div>
        </GlassCard>
      </div>
    </div>
  );
}

function PaymentsTab({ customer }: { customer: Customer360Record }) {
  const { l, lang } = useI18n();
  const totalCaptured = customer.payments
    .filter((payment) => payment.status === 'captured')
    .reduce((sum, payment) => sum + payment.amountFils, 0);
  const columns: DataTableColumn<Customer360Record['payments'][number]>[] = [
    {
      id: 'application',
      header: l({ en: 'Application', ar: 'الطلب' }),
      sortable: true,
      value: (row) => row.applicationReference,
      render: (row) => (
        <div>
          <div className="font-mono text-xs font-bold">{row.applicationReference}</div>
          <div className="mt-0.5 max-w-72 truncate text-xs text-ink-faint">
            {l(row.serviceName)}
          </div>
        </div>
      ),
    },
    {
      id: 'amount',
      header: l({ en: 'Amount', ar: 'المبلغ' }),
      sortable: true,
      value: (row) => row.amountFils,
      render: (row) => (
        <span className="font-mono font-bold">
          {(row.amountFils / 100).toLocaleString(undefined, { minimumFractionDigits: 2 })}{' '}
          {row.currency}
        </span>
      ),
    },
    {
      id: 'status',
      header: l(S.status),
      sortable: true,
      value: (row) => row.status,
      render: (row) => (
        <StatusPill
          state={
            row.status === 'captured'
              ? 'published'
              : row.status === 'failed'
                ? 'rejected'
                : 'in_progress'
          }
          label={row.status}
        />
      ),
    },
    {
      id: 'provider',
      header: l({ en: 'Provider', ar: 'المزود' }),
      sortable: true,
      value: (row) => row.provider,
      render: (row) => (
        <div>
          <div className="font-semibold">{row.provider}</div>
          <div className="font-mono text-[10px] text-ink-faint">
            {row.providerReference ?? '—'}
          </div>
        </div>
      ),
    },
    {
      id: 'card',
      header: l({ en: 'Payment method', ar: 'وسيلة الدفع' }),
      render: (row) => (
        <span className="text-xs">
          {row.cardBrand ?? '—'} {row.cardLast4 ? `•••• ${row.cardLast4}` : ''}
        </span>
      ),
    },
    {
      id: 'created',
      header: l({ en: 'Created', ar: 'الإنشاء' }),
      sortable: true,
      value: (row) => row.createdAt,
      render: (row) => <span className="text-xs">{formatDate(row.createdAt, lang)}</span>,
    },
  ];
  return (
    <div className="space-y-4">
      <div className="grid gap-3 sm:grid-cols-3">
        <MetricCard
          label={l({ en: 'Payment intents', ar: 'طلبات الدفع' })}
          value={customer.payments.length}
        />
        <MetricCard
          label={l({ en: 'Captured value', ar: 'القيمة المحصلة' })}
          value={formatFils(totalCaptured, lang)}
          tone="success"
        />
        <MetricCard
          label={l({ en: 'Refunded', ar: 'المسترد' })}
          value={customer.payments.filter((payment) => payment.status === 'refunded').length}
          tone="warning"
        />
      </div>
      <GlassCard className="p-5">
        <SectionTitle
          icon="document"
          title={l({ en: 'Payment history', ar: 'سجل المدفوعات' })}
          count={customer.payments.length}
        />
        <DataTable
          rows={customer.payments}
          columns={columns}
          rowKey={(row) => row.id}
          emptyTitle={l({ en: 'No payment history', ar: 'لا يوجد سجل مدفوعات' })}
          filename={`customer-${customer.id}-payments.csv`}
        />
      </GlassCard>
    </div>
  );
}

function DocumentsTab({ customer }: { customer: Customer360Record }) {
  const { l, lang } = useI18n();
  const columns: DataTableColumn<Customer360Record['documents'][number]>[] = [
    { id: 'document', header: l(S.documentsLabel), sortable: true, value: (row) => row.filename ?? row.kind, render: (row) => <div><div className="font-semibold">{row.filename ?? row.kind}</div><div className="text-[10px] text-ink-faint">{row.kind}</div></div> },
    { id: 'status', header: l(S.status), sortable: true, value: (row) => row.status, render: (row) => <StatusPill state={row.status === 'verified' ? 'published' : row.status === 'rejected' ? 'rejected' : 'in_review'} label={row.status} /> },
    { id: 'confidence', header: l(S.confidence), sortable: true, value: (row) => row.confidence, render: (row) => <span className="font-mono">{row.confidence == null ? '—' : `${Math.round(row.confidence * 100)}%`}</span> },
    { id: 'issued', header: l({ en: 'Issued', ar: 'الإصدار' }), sortable: true, value: (row) => row.issuedAt, render: (row) => <span>{row.issuedAt ?? '—'}</span> },
    { id: 'expires', header: l(S.expires), sortable: true, value: (row) => row.expiresAt, render: (row) => <span className={row.expiresAt && new Date(row.expiresAt) < new Date() ? 'font-bold text-reject' : ''}>{row.expiresAt ?? '—'}</span> },
    { id: 'added', header: l({ en: 'Added', ar: 'تمت الإضافة' }), sortable: true, value: (row) => row.createdAt, render: (row) => <span className="text-xs">{formatDate(row.createdAt, lang)}</span> },
    { id: 'extracted', header: l(S.extracted), render: (row) => <span className="block max-w-md truncate text-xs text-ink-soft" title={JSON.stringify(row.extracted)}>{Object.entries(row.extracted).map(([key, value]) => `${key}: ${value}`).join(' · ') || '—'}</span> },
  ];
  return (
    <GlassCard className="p-5">
      <SectionTitle icon="document" title={l({ en: 'Document vault', ar: 'خزنة المستندات' })} count={customer.documents.length} />
      <DataTable rows={customer.documents} columns={columns} rowKey={(row) => row.id} emptyTitle={l({ en: 'No vault documents', ar: 'لا توجد مستندات في الخزنة' })} filename={`customer-${customer.id}-documents.csv`} />
    </GlassCard>
  );
}

function IdentityTab({ customer }: { customer: Customer360Record }) {
  const { l, lang } = useI18n();
  return (
    <div className="grid gap-4 xl:grid-cols-2">
      <GlassCard className="p-5">
        <SectionTitle icon="identity" title={l({ en: 'Verified identities', ar: 'الهويات المتحقق منها' })} count={customer.identities.length} />
        <div className="space-y-2">
          {customer.identities.map((identity) => (
            <div key={identity.id} className="flex items-center gap-3 rounded-lg border border-aeblack-100 p-3">
              <span className="grid h-9 w-9 place-items-center rounded-full bg-aegreen-50 text-approve"><CrmIcon name="identity" /></span>
              <div className="min-w-0 flex-1"><div className="font-bold">{identity.kind}</div><div className="text-xs text-ink-faint">{identity.channel} · {formatDate(identity.proofed_at, lang)}</div></div>
              <StatusPill state="published" label={l({ en: 'Verified', ar: 'متحقق' })} />
            </div>
          ))}
          {customer.identities.length === 0 && <EmptyState title={l({ en: 'No verified identities', ar: 'لا توجد هويات متحقق منها' })} />}
        </div>
      </GlassCard>
      <ConsentManagerCard customer={customer} />
      <GlassCard className="p-5 xl:col-span-2">
        <SectionTitle icon="identity" title={l({ en: 'Customer profile attributes', ar: 'سمات ملف المتعامل' })} />
        <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
          {Object.entries(customer.profile).map(([key, value]) => (
            <div key={key} className="rounded-lg bg-aeblack-50 p-3">
              <div className="text-[10px] font-bold tracking-wide text-ink-faint uppercase">{key.replaceAll('_', ' ')}</div>
              <div className="mt-1 break-words text-sm font-semibold">{typeof value === 'object' ? JSON.stringify(value) : String(value ?? '—')}</div>
            </div>
          ))}
          {Object.keys(customer.profile).length === 0 && <div className="text-sm text-ink-faint">{l(S.none)}</div>}
        </div>
      </GlassCard>
    </div>
  );
}

/* --------------------------------------------------------------------------
 * ConsentManagerCard — PDPL consent register (plan point 38). The API exposes
 * no console-side revoke mutation, so the register is read-only with a
 * bilingual note explaining where consent changes happen.
 * ------------------------------------------------------------------------- */
function ConsentManagerCard({ customer }: { customer: Customer360Record }) {
  const { l, lang } = useI18n();
  return (
    <SectionCard
      accent={SECTION_ACCENTS.engagement}
      title={l(S.consentManager)}
      hint={l(S.consentManagerHint)}
      actions={<span className="aegov-badge badge-info">PDPL</span>}
    >
      <div className="space-y-2">
        {customer.consents.map((consent) => (
          <div key={consent.id} className="rounded-lg border border-aeblack-100 p-3">
            <div className="flex flex-wrap items-center gap-2">
              <span className="font-bold">{consent.scope}</span>
              <StatusPill
                state={consent.revoked_at ? 'rejected' : 'published'}
                label={consent.revoked_at ? l(S.revoked) : l({ en: 'Active', ar: 'نشطة' })}
              />
              <span className="ms-auto text-[10px] text-ink-faint">
                {l(S.consentVia)} {consent.granted_via}
              </span>
            </div>
            <div className="mt-1 text-sm text-ink-soft">{l(consent.purpose)}</div>
            <div className="mt-1 flex flex-wrap gap-x-4 gap-y-0.5 text-[10px] text-ink-faint">
              <span>{l(S.granted)}: {formatDate(consent.granted_at, lang)}</span>
              {consent.revoked_at && (
                <span className="font-semibold text-reject">
                  {l(S.revokedOn)}: {formatDate(consent.revoked_at, lang)}
                </span>
              )}
            </div>
          </div>
        ))}
        {customer.consents.length === 0 && (
          <EmptyState title={l({ en: 'No consent records', ar: 'لا توجد سجلات موافقة' })} />
        )}
      </div>
      <div className="mt-3 flex items-start gap-2 rounded-lg border border-seablue-100 bg-seablue-50 p-3 text-[10px] font-semibold leading-relaxed text-seablue-700">
        <span aria-hidden className="mt-0.5 shrink-0"><Icon name="info" size={13} /></span>
        {l(S.consentReadOnlyNote)}
      </div>
    </SectionCard>
  );
}

function CrmProfileEditor({
  customer,
  busy,
  onSave,
  onCancel,
}: {
  customer: Customer360Record;
  busy: boolean;
  onSave: (input: Partial<CustomerCrmProfile>) => Promise<void>;
  onCancel: () => void;
}) {
  const { l } = useI18n();
  const [ownerId, setOwnerId] = useState(customer.crm.ownerId ?? '');
  const [lifecycleStage, setLifecycleStage] = useState(customer.crm.lifecycleStage);
  const [riskLevel, setRiskLevel] = useState(customer.crm.riskLevel);
  const [tags, setTags] = useState(customer.crm.tags.join(', '));
  const [nextFollowUpAt, setNextFollowUpAt] = useState(
    customer.crm.nextFollowUpAt?.slice(0, 16) ?? '',
  );
  return (
    <GlassCard className="mb-4 border-seablue-100 p-5">
      <SectionTitle icon="identity" title={l({ en: 'CRM relationship settings', ar: 'إعدادات علاقة المتعامل' })} />
      <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-5">
        <label><span className="mb-1 block text-xs font-bold">{l({ en: 'Owner', ar: 'المسؤول' })}</span><GlassSelect value={ownerId} onChange={(event) => setOwnerId(event.target.value)}><option value="">{l({ en: 'Unassigned', ar: 'غير معيّن' })}</option>{customer.staffOptions.map((staff) => <option key={staff.id} value={staff.id}>{staff.displayName}</option>)}</GlassSelect></label>
        <label><span className="mb-1 block text-xs font-bold">{l({ en: 'Lifecycle', ar: 'مرحلة العلاقة' })}</span><GlassSelect value={lifecycleStage} onChange={(event) => setLifecycleStage(event.target.value as CustomerCrmProfile['lifecycleStage'])}><option value="new">new</option><option value="active">active</option><option value="at_risk">at risk</option><option value="vip">VIP</option><option value="inactive">inactive</option></GlassSelect></label>
        <label><span className="mb-1 block text-xs font-bold">{l({ en: 'Risk', ar: 'المخاطر' })}</span><GlassSelect value={riskLevel} onChange={(event) => setRiskLevel(event.target.value as CustomerCrmProfile['riskLevel'])}><option value="low">low</option><option value="standard">standard</option><option value="elevated">elevated</option><option value="critical">critical</option></GlassSelect></label>
        <label><span className="mb-1 block text-xs font-bold">{l({ en: 'Next follow-up', ar: 'المتابعة التالية' })}</span><GlassInput type="datetime-local" value={nextFollowUpAt} onChange={(event) => setNextFollowUpAt(event.target.value)} /></label>
        <label><span className="mb-1 block text-xs font-bold">{l({ en: 'Tags', ar: 'الوسوم' })}</span><GlassInput value={tags} onChange={(event) => setTags(event.target.value)} /></label>
      </div>
      <div className="mt-4 flex flex-wrap justify-end gap-2">
        <GlassButton variant="ghost" onClick={onCancel}>{l(S.cancel)}</GlassButton>
        <GlassButton variant="primary" disabled={busy} onClick={() => void onSave({
          ownerId: ownerId || null,
          lifecycleStage,
          riskLevel,
          tags: tags.split(',').map((tag) => tag.trim()).filter(Boolean),
          nextFollowUpAt: nextFollowUpAt || null,
        })}>{l({ en: 'Save CRM profile', ar: 'حفظ ملف CRM' })}</GlassButton>
      </div>
    </GlassCard>
  );
}
